import type { LucideIcon } from 'lucide-react'
import {
  Users, Cable, KeyRound, FileCode2, Fingerprint,
  Puzzle, Bell, Settings, ClipboardList,
  Smartphone, Coins, CreditCard, Activity, Bot, ScrollText, BarChart3, MessagesSquare, PlugZap,
  LayoutDashboard, Building2, Server, Palette, Boxes, CircleDollarSign,
  BookOpen, Clock, Rss, Webhook, Database, ClipboardCheck, Network, Workflow
} from 'lucide-react'
import type { NavigableResources, NavigableWorker, OrganizationSummary } from '@/frontend/types/inertia'

type ItemGroup =
  | 'ir-para' | 'conversas' | 'acoes'
  | 'dashboard' | 'admin'
  | 'organizacoes' | 'agentes' | 'credenciais' | 'composio'
  | 'admin-agentes' | 'admin-instancias' | 'admin-telemetria' | 'admin-wabas' | 'admin-orgs'

export interface CommandItem {
  id: string
  label: string
  href: string
  icon: LucideIcon
  group: ItemGroup
  keywords?: string[]
  subtitle?: string
  // Dica visual de atalho (⌘1..⌘9, etc.) exibida à direita do item (SZ-1594).
  shortcut?: string
}

const WORKER_TABS = [
  { key: 'geral', label: 'Geral' },
  { key: 'comunicacao', label: 'Comunicação' },
  { key: 'uso', label: 'Uso' },
  { key: 'rotinas', label: 'Rotinas' },
  { key: 'memoria', label: 'Memória' },
  // Sem Auditoria: saiu da navegação do agente, e getResourceItems gerava um
  // "{agente} › Auditoria" para cada worker, reabrindo a porta pelo ⌘K.
] as const

const ADMIN_WORKER_TABS = [
  { key: 'sessoes', label: 'Sessões' },
  { key: 'rotinas', label: 'Rotinas' },
  { key: 'memoria', label: 'Memória' },
  { key: 'allowlist', label: 'Allowlist' },
  { key: 'config', label: 'Config' },
] as const

// Seções do agente selecionado para o grupo "Ir para" da paleta (SZ-1594).
// Lista definida aqui: espelha os destinos da navegação do portal
// (layouts/worker-first/workerGroups.ts, primeiro nível na sidebar e irmãs
// profundas no WorkerPortalNav) — seções com rota própria (SZ-1559) e
// páginas globais filtradas (SZ-1561). As `keys` sem acento entram como
// keywords para a busca funcionar sem diacríticos.
interface WorkerSection {
  key: string
  label: string
  icon: LucideIcon
  href: (workerId: string) => string
  // Seções com gate de instância só aparecem com o worker pronto, como no nav.
  gate?: 'instance'
  // Processos segue a mesma feature flag da navegação do portal.
  flag?: 'business_processes'
}

const showPath = (workerId: string) => `/dashboard/digital_workers/${workerId}`

const WORKER_SECTIONS: WorkerSection[] = [
  { key: 'visao-geral', label: 'Visão geral', icon: LayoutDashboard, href: showPath },
  {
    key: 'processos', label: 'Processos', icon: Workflow, flag: 'business_processes',
    href: (id) => `/dashboard/business_processes?digital_worker_id=${id}`,
  },
  { key: 'rotinas', label: 'Rotinas', icon: Clock, gate: 'instance', href: (id) => `${showPath(id)}/rotinas` },
  // Publicações saiu das seções do agente: virou item org da sidebar
  // (/dashboard/artifacts, já na paleta como item global).
  { key: 'uso', label: 'Uso', icon: BarChart3, href: (id) => `${showPath(id)}/uso` },
  { key: 'comunicacao', label: 'Comunicação', icon: Rss, href: (id) => `${showPath(id)}/comunicacao` },
  { key: 'integracoes', label: 'Integrações', icon: Cable, href: (id) => `/dashboard/integrations?digital_worker_id=${id}` },
  { key: 'credenciais', label: 'Credenciais', icon: KeyRound, href: (id) => `/dashboard/credentials?digital_worker_id=${id}` },
  { key: 'webhooks', label: 'Webhooks', icon: Webhook, gate: 'instance', href: (id) => `${showPath(id)}/webhooks` },
  { key: 'memoria', label: 'Memória', icon: Database, gate: 'instance', href: (id) => `${showPath(id)}/memoria` },
  { key: 'evals', label: 'Evals', icon: ClipboardCheck, gate: 'instance', href: (id) => `${showPath(id)}/evals` },
  // Auditoria saiu da navegação do agente; o item org (/dashboard/audit_logs)
  // continua na paleta.
  { key: 'configuracoes', label: 'Configurações', icon: Settings, href: (id) => `${showPath(id)}/configuracoes` },
]

// Itens do grupo "Ir para" da paleta: as seções do agente selecionado.
export function getSelectedWorkerItems(
  worker: NavigableWorker,
  options: { businessProcessesEnabled: boolean }
): CommandItem[] {
  const workerReady = worker.provisioning_status === 'ready'

  return WORKER_SECTIONS
    .filter((section) => section.flag !== 'business_processes' || options.businessProcessesEnabled)
    .filter((section) => section.gate !== 'instance' || workerReady)
    .map((section) => ({
      id: `ir-para-${worker.id}-${section.key}`,
      label: section.label,
      href: section.href(worker.id),
      icon: section.icon,
      group: 'ir-para' as const,
      keywords: [section.key.replace(/-/g, ' '), worker.name, 'ir para'],
    }))
}

const COMPOSIO_SLUG_NAMES: Record<string, string> = {
  github: 'GitHub', gmail: 'Gmail', slack: 'Slack', notion: 'Notion',
  googlesheets: 'Google Sheets', shopify: 'Shopify', googledrive: 'Google Drive',
  supabase: 'Supabase', hubspot: 'HubSpot', active_campaign: 'ActiveCampaign',
  affinity: 'Affinity', ahrefs: 'Ahrefs', airtable: 'Airtable', amplitude: 'Amplitude',
  apollo: 'Apollo', asana: 'Asana', attio: 'Attio', basecamp: 'Basecamp',
  bubble: 'Bubble', cal: 'Cal.com', calendly: 'Calendly', canva: 'Canva',
  clickup: 'ClickUp', clicksend: 'ClickSend', cloudflare: 'Cloudflare',
  coda: 'Coda', confluence: 'Confluence', datadog: 'Datadog',
  digital_ocean: 'DigitalOcean', docusign: 'DocuSign', dropbox: 'Dropbox',
  elevenlabs: 'ElevenLabs', figma: 'Figma', firecrawl: 'Firecrawl',
  gamma: 'Gamma', gitlab: 'GitLab', googleads: 'Google Ads',
  google_analytics: 'Google Analytics', googlecalendar: 'Google Calendar',
  googledocs: 'Google Docs', google_maps: 'Google Maps',
  googlemeet: 'Google Meet', google_search_console: 'Google Search Console',
  googleslides: 'Google Slides', googletasks: 'Google Tasks',
  grafana: 'Grafana', instagram: 'Instagram', jira: 'Jira', linear: 'Linear',
  linkedin: 'LinkedIn', linkedin_ads: 'LinkedIn Ads', mailchimp: 'Mailchimp',
  mailersend: 'MailerSend', make: 'Make', miro: 'Miro', monday: 'Monday.com',
  one_drive: 'OneDrive', outlook: 'Outlook', pipedrive: 'Pipedrive',
  quickbooks: 'QuickBooks', remove_bg: 'Remove.bg', resend: 'Resend',
  salesforce: 'Salesforce', semrush: 'SEMrush', sendgrid: 'SendGrid',
  share_point: 'SharePoint', snowflake: 'Snowflake', spotify: 'Spotify',
  stripe: 'Stripe', survey_monkey: 'SurveyMonkey', tally: 'Tally',
  tiktok: 'TikTok', tinypng: 'TinyPNG', tldv: 'tl;dv', trello: 'Trello',
  twitter: 'Twitter', vercel: 'Vercel', webflow: 'Webflow', zendesk: 'Zendesk',
  zoho: 'Zoho', zoho_mail: 'Zoho Mail', zoho_desk: 'Zoho Desk', zoom: 'Zoom',
  intercom: 'Intercom', youtube: 'YouTube',
}

function composioDisplayName(slug: string): string {
  return COMPOSIO_SLUG_NAMES[slug] ?? slug.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function getCommandItems(options: {
  orgSlug: string | null
  organizations: OrganizationSummary[]
  isAdmin: boolean
  discoveryLabel: string
  canAccessForge: boolean
}): CommandItem[] {
  const items: CommandItem[] = [
    {
      id: 'dashboard-digital-workers-new',
      label: 'Criar agente',
      href: '/dashboard/digital_workers/new',
      icon: Bot,
      group: 'dashboard',
      keywords: ['novo agente', 'criar worker', 'time'],
    },
    {
      id: 'dashboard-agent-chats',
      label: 'Chat',
      href: '/dashboard/agent_chats',
      icon: MessagesSquare,
      group: 'dashboard',
      keywords: ['chat', 'mensagens', 'conversas'],
    },
    {
      id: 'dashboard-integrations',
      label: 'Integrações',
      href: '/dashboard/integrations',
      icon: Cable,
      group: 'dashboard',
      keywords: ['integrations', 'tools', 'ferramentas'],
    },
    {
      id: 'dashboard-integrations-meta',
      label: 'Meta',
      href: '/dashboard/integrations/meta',
      icon: Cable,
      group: 'dashboard',
      keywords: ['facebook', 'ads', 'instagram', 'meta'],
    },
    {
      id: 'dashboard-integrations-google',
      label: 'Google',
      href: '/dashboard/integrations/google',
      icon: Cable,
      group: 'dashboard',
      keywords: ['analytics', 'ads', 'search console', 'google'],
    },
    {
      id: 'dashboard-integrations-notion',
      label: 'Notion',
      href: '/dashboard/integrations/notion',
      icon: Cable,
      group: 'dashboard',
      keywords: ['notion', 'wiki', 'integração nativa'],
    },
    {
      id: 'dashboard-composio-notion',
      label: 'Notion (Composio)',
      href: '/dashboard/composio/notion',
      icon: Puzzle,
      group: 'composio',
      keywords: ['notion', 'wiki', 'composio'],
    },
    {
      id: 'dashboard-integrations-slack',
      label: 'Slack',
      href: '/dashboard/integrations/slack',
      icon: Cable,
      group: 'dashboard',
      keywords: ['slack', 'mensagens'],
    },
    {
      id: 'dashboard-integrations-microvix',
      label: 'Microvix',
      href: '/dashboard/integrations/microvix',
      icon: Cable,
      group: 'dashboard',
      keywords: ['microvix', 'erp', 'integração'],
    },
    {
      id: 'dashboard-integrations-requests',
      label: 'Solicitar Integração',
      href: '/dashboard/integrations/requests',
      icon: Cable,
      group: 'dashboard',
      keywords: ['solicitar', 'nova integração', 'requests', 'pedir'],
    },
    {
      id: 'dashboard-avatar-generator',
      label: 'Gerador de Avatar',
      href: '/dashboard/avatar_generator',
      icon: Palette,
      group: 'dashboard',
      keywords: ['avatar', 'imagem', 'pixel art', 'gerar'],
    },
    {
      id: 'dashboard-credentials',
      label: 'Credenciais',
      href: '/dashboard/credentials',
      icon: KeyRound,
      group: 'dashboard',
      keywords: ['chaves', 'api', 'credentials'],
    },
    {
      id: 'dashboard-artifacts',
      label: 'Publicações',
      href: '/dashboard/artifacts',
      icon: FileCode2,
      group: 'dashboard',
      keywords: ['artifacts', 'publicacoes'],
    },
    {
      id: 'dashboard-audit-logs',
      label: 'Auditoria',
      href: '/dashboard/audit_logs',
      icon: Fingerprint,
      group: 'dashboard',
      keywords: ['logs', 'audit'],
    },
    {
      id: 'dashboard-activity-logs',
      label: 'Atividades',
      href: '/dashboard/activity_logs',
      icon: ClipboardList,
      group: 'dashboard',
      keywords: ['activity', 'logs', 'atividade'],
    },
    {
      id: 'dashboard-notifications',
      label: 'Notificações',
      href: '/dashboard/notifications',
      icon: Bell,
      group: 'dashboard',
      keywords: ['alertas', 'notifications'],
    },
  ]

  if (options.canAccessForge === true) {
    items.push({
      id: 'dashboard-discovery',
      label: options.discoveryLabel,
      href: '/dashboard/discovery',
      icon: Network,
      group: 'dashboard',
      keywords: ['discovery', 'mapa', 'organização', 'fde', 'ontology'],
    })
  }

  if (options.orgSlug) {
    items.push({
      id: 'dashboard-settings',
      label: 'Configurações',
      href: `/dashboard/organizations/${options.orgSlug}`,
      icon: Settings,
      group: 'dashboard',
      keywords: ['organização', 'settings', 'config'],
    })
  }

  for (const org of options.organizations) {
    items.push({
      id: `dashboard-organization-${org.id}`,
      label: org.name,
      subtitle: org.slug === options.orgSlug ? 'Organização atual' : 'Organização',
      href: `/dashboard/organizations/${org.slug}`,
      icon: Building2,
      group: 'organizacoes',
      keywords: [org.slug, 'organização', 'configurações'],
    })
  }

  items.push({
    id: 'dashboard-organizations-new',
    label: 'Nova organização',
    href: '/dashboard/organizations/new',
    icon: Building2,
    group: 'organizacoes',
    keywords: ['organização', 'nova org', 'criar organização'],
  })

  if (options.isAdmin) {
    items.push(
      {
        id: 'admin-dashboard',
        label: 'Painel',
        href: '/cc',
        icon: LayoutDashboard,
        group: 'admin',
        keywords: ['dashboard', 'painel', 'visão geral', 'overview'],
      },
      {
        id: 'admin-settings',
        label: 'Configurações (CC)',
        href: '/cc/settings',
        icon: Settings,
        group: 'admin',
        keywords: ['settings', 'configurações', 'plataforma'],
      },
      {
        id: 'admin-organizations',
        label: 'Organizações (CC)',
        href: '/cc/organizations',
        icon: Building2,
        group: 'admin',
        keywords: ['organizações', 'orgs', 'organizations'],
      },
      {
        id: 'admin-users',
        label: 'Usuários',
        href: '/cc/users',
        icon: Users,
        group: 'admin',
        keywords: ['users'],
      },
      {
        id: 'admin-academy',
        label: 'Academy (CC)',
        href: '/cc/academy/articles',
        icon: BookOpen,
        group: 'admin',
        keywords: ['artigos', 'conteúdo', 'academy'],
      },
      {
        id: 'admin-integrations',
        label: 'Integrações (CC)',
        href: '/cc/integrations',
        icon: PlugZap,
        group: 'admin',
        keywords: ['composio', 'integrações', 'apps'],
      },
      {
        id: 'admin-wabas',
        label: 'WABAs',
        href: '/cc/wabas',
        icon: Smartphone,
        group: 'admin',
        keywords: ['whatsapp'],
      },
      {
        id: 'admin-billing-plans',
        label: 'Planos de Cobrança',
        href: '/cc/plans',
        icon: CreditCard,
        group: 'admin',
        keywords: ['stripe', 'billing', 'cobrança', 'assinaturas'],
      },
      {
        id: 'admin-token-plans',
        label: 'Planos',
        href: '/cc/token_plans',
        icon: Coins,
        group: 'admin',
        keywords: ['tokens', 'plans'],
      },
      {
        id: 'admin-fleet-machines',
        label: 'Máquinas',
        href: '/cc/fleet_machines',
        icon: Server,
        group: 'admin',
        keywords: ['fleet', 'machines', 'máquinas', 'servidores'],
      },
      {
        id: 'admin-instances',
        label: 'Instâncias',
        href: '/cc/instances',
        icon: Boxes,
        group: 'admin',
        keywords: ['instances', 'instâncias', 'fleet', 'openclaw'],
      },
      {
        id: 'admin-telemetry',
        label: 'Telemetria',
        href: '/cc/telemetry',
        icon: Activity,
        group: 'admin',
        keywords: ['metrics', 'telemetry'],
      },
      {
        id: 'admin-litellm-costs',
        label: 'Custos IA',
        href: '/cc/litellm_costs',
        icon: CircleDollarSign,
        group: 'admin',
        keywords: ['litellm', 'custos', 'spend'],
      },
      {
        id: 'admin-digital-workers',
        label: 'Agentes',
        href: '/cc/digital_workers',
        icon: Bot,
        group: 'admin',
        keywords: ['agents', 'workers'],
      },
      {
        id: 'admin-audit-logs',
        label: 'Auditoria (CC)',
        href: '/cc/audit_logs',
        icon: ScrollText,
        group: 'admin',
        keywords: ['logs', 'audit'],
      },
      {
        id: 'admin-panorama',
        label: 'Panorama',
        href: '/cc/panorama_responses',
        icon: BarChart3,
        group: 'admin',
        keywords: ['responses', 'analytics'],
      },
    )
  }

  return items
}

export function getResourceItems(resources: NavigableResources | null): CommandItem[] {
  if (!resources) return []

  const items: CommandItem[] = []

  for (const worker of resources.workers) {
    items.push({
      id: `worker-${worker.id}`,
      label: worker.name,
      subtitle: worker.job_title ?? undefined,
      href: `/dashboard/digital_workers/${worker.id}`,
      icon: Bot,
      group: 'agentes',
    })

    for (const tab of WORKER_TABS) {
      items.push({
        id: `worker-${worker.id}-${tab.key}`,
        label: `${worker.name} › ${tab.label}`,
        href: `/dashboard/digital_workers/${worker.id}?tab=${tab.key}`,
        icon: Bot,
        group: 'agentes',
        keywords: [worker.name, tab.label],
      })
    }
  }

  for (const credential of resources.credentials) {
    items.push({
      id: `credential-${credential.id}`,
      label: credential.name,
      href: `/dashboard/credentials/${credential.id}`,
      icon: KeyRound,
      group: 'credenciais',
    })
  }

  for (const slug of resources.composioToolkits) {
    if (slug === 'notion') continue

    items.push({
      id: `composio-${slug}`,
      label: composioDisplayName(slug),
      href: `/dashboard/composio/${slug}`,
      icon: Puzzle,
      group: 'composio',
      keywords: [slug],
    })
  }

  if (resources.adminWorkers) {
    for (const worker of resources.adminWorkers) {
      items.push({
        id: `admin-worker-${worker.id}`,
        label: worker.name,
        subtitle: worker.org_name,
        href: `/cc/digital_workers/${worker.id}`,
        icon: Bot,
        group: 'admin-agentes',
      })

      for (const tab of ADMIN_WORKER_TABS) {
        items.push({
          id: `admin-worker-${worker.id}-${tab.key}`,
          label: `${worker.name} › ${tab.label}`,
          href: `/cc/digital_workers/${worker.id}?tab=${tab.key}`,
          icon: Bot,
          group: 'admin-agentes',
          keywords: [worker.name, tab.label],
        })
      }

      items.push({
        id: `admin-instance-${worker.id}`,
        label: worker.instance_name || worker.name,
        subtitle: worker.org_name,
        href: `/cc/instances/${worker.id}`,
        icon: Boxes,
        group: 'admin-instancias',
        keywords: [worker.name, worker.instance_name, worker.org_name, 'instância']
          .filter((keyword): keyword is string => Boolean(keyword)),
      })

      items.push({
        id: `admin-worker-telemetry-${worker.id}`,
        label: worker.name,
        subtitle: worker.org_name,
        href: `/cc/telemetry/digital_workers/${worker.id}`,
        icon: Activity,
        group: 'admin-telemetria',
        keywords: [worker.name, worker.instance_name, worker.org_name, 'telemetria']
          .filter((keyword): keyword is string => Boolean(keyword)),
      })
    }
  }

  if (resources.adminWabas) {
    for (const waba of resources.adminWabas) {
      items.push({
        id: `admin-waba-${waba.id}`,
        label: waba.name,
        subtitle: waba.org_name,
        href: `/cc/wabas/${waba.id}`,
        icon: Smartphone,
        group: 'admin-wabas',
      })
    }
  }

  if (resources.adminOrganizations) {
    for (const org of resources.adminOrganizations) {
      items.push({
        id: `admin-org-${org.id}`,
        label: org.name,
        subtitle: 'Telemetria',
        href: `/cc/telemetry/organizations/${org.slug}`,
        icon: BarChart3,
        group: 'admin-orgs',
        keywords: [org.slug, 'telemetria'],
      })
    }
  }

  return items
}
